// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import styled from 'styled-components';
import Button from '../components/Button';

export const ProductDetail = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 24px 0 48px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 56px 32px 64px;
  }
`;

export const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;

  ${({ theme }) => theme.breakpoints.desktop} {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: start;
  }
`;

export const ImageWell = styled.div`
  margin: 0 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;

  ${({ theme }) => theme.breakpoints.desktop} {
    margin: 0;
    position: sticky;
    top: 96px;
  }
`;

export const Image = styled.div<{ $src: string }>`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: url(${({ $src }) => $src}) no-repeat center;
  background-size: contain;
`;

export const Details = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 0;
  }
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  p {
    margin: 0;
  }
`;

export const BadgePill = styled.span<{ $variant: string }>`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $variant, theme }) => ($variant === 'Low stock' || $variant === 'New' ? theme.colors.ink : theme.colors.white)};
  background: ${({ $variant, theme }) =>
    ($variant === 'Bestseller' && theme.colors.ink) ||
    ($variant === 'Sale' && '#d6336c') ||
    ($variant === 'Staff pick' && theme.colors.otelBlue) ||
    ($variant === 'Low stock' && theme.colors.otelYellow) ||
    'linear-gradient(93deg, #f46800 0%, #ff8833 100%)'};
`;

export const Category = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const Name = styled.h1`
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.ink};

  ${({ theme }) => theme.breakpoints.desktop} {
    font-size: 36px;
  }
`;

export const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;

  > span:last-child {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;

export const Stars = styled.span`
  display: inline-flex;
  letter-spacing: 1px;
`;

export const Star = styled.span<{ $filled: boolean }>`
  font-size: 15px;
  color: ${({ theme, $filled }) => ($filled ? theme.colors.grafanaOrange : theme.colors.border)};
`;

export const ProductPrice = styled.p`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin: 0 0 16px;
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ink};

  s {
    font-size: 18px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;

export const Description = styled.p`
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.inkMuted};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 20px;
`;

export const Stock = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1a7f37;
  }
`;

export const PurchaseRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 20px;
`;

export const Text = styled.p`
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
`;

export const AddToCart = styled(Button)`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
`;

export const TrustList = styled.ul`
  margin: 0;
  padding: 16px 0 0;
  list-style: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.inkMuted};

    img {
      width: 14px;
      height: 14px;
    }
  }
`;
